import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  Text,
  Input,
  Button,
  SimpleGrid,
  Avatar,
  Spinner,
  useToast,
  Flex,
  VStack,
  IconButton,
  Badge,
} from "@chakra-ui/react";
import { DeleteIcon } from "@chakra-ui/icons";
import { useAuth } from "../context/AuthContext";
import Layout from "../components/Layout";
import client from "../utils/client";
import { useForm } from "react-hook-form";

const bucket = process.env.REACT_APP_S3_BUCKET;
const region = process.env.REACT_APP_S3_REGION;
const folder = "user_images";

const AdminSection = React.memo(({ onAdd }) => {
  const [users, setUsers] = useState([]);
  const { register, watch } = useForm({ defaultValues: { searchTerm: "" } });
  const searchTerm = watch("searchTerm");
  const [filtered, setFiltered] = useState([]);

  useEffect(() => {
    const request = async () => {
      const response = await client.get("/users");
      response.data && setUsers(response.data);
    };
    request();
  }, []);

  useEffect(() => {
    if (searchTerm && searchTerm.length > 2) {
      setFiltered(
        users.filter((u) =>
          u.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    } else {
      setFiltered([]);
    }
  }, [searchTerm, users]);

  return (
    <Box
      maxW="560px"
      mx="auto"
      mb={10}
      bg="white"
      borderRadius="1.25rem"
      boxShadow="0 2px 12px rgba(0,0,0,0.07)"
      border="1px solid"
      borderColor="green.100"
      overflow="hidden"
    >
      <Box bg="green.600" px={6} py={4}>
        <Heading size="sm" color="white">Adicionar Colaborador</Heading>
        <Text fontSize="0.82rem" color="green.100" mt={1}>Busque um membro pelo nome para incluir na lista</Text>
      </Box>
      <Box p={5}>
        <Input
          placeholder="Buscar usuário por nome..."
          {...register("searchTerm")}
          bg="gray.50"
          borderColor="gray.200"
          _focus={{ borderColor: "green.400", bg: "white" }}
        />

        {filtered.length > 0 && (
          <VStack mt={4} align="stretch" spacing={2}>
            {filtered.map((u) => (
              <Flex
                key={u.userId || u.id}
                justify="space-between"
                align="center"
                p={3}
                borderRadius="0.75rem"
                bg="gray.50"
                border="1px solid"
                borderColor="gray.100"
                _hover={{ borderColor: "green.200", bg: "green.50" }}
                transition="all 0.15s"
              >
                <Flex align="center" gap={3}>
                  <Avatar
                    size="sm"
                    src={`https://${bucket}.s3.${region}.amazonaws.com/${folder}/${u.photo}`}
                  />
                  <Box>
                    <Text fontWeight={700} fontSize="0.9rem">{u.name}</Text>
                    {u.lodge && <Text fontSize="0.75rem" color="gray.500">{u.lodge}</Text>}
                  </Box>
                </Flex>
                <Button colorScheme="green" size="xs" onClick={() => onAdd(u)}>
                  Adicionar
                </Button>
              </Flex>
            ))}
          </VStack>
        )}
      </Box>
    </Box>
  );
});

const Colaborators = () => {
  const { user } = useAuth();
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    const fetchCollaborators = async () => {
      setLoading(true);
      const { data } = await client.get("/colaborators");
      setCollaborators(data);
      setLoading(false);
    };
    fetchCollaborators();
  }, []);

  const handleAddCollaborator = async (userToAdd) => {
    setLoading(true);
    if (collaborators.some((c) => c.userId === userToAdd.userId)) {
      toast({ title: "Usuário já é um colaborador.", status: "warning", duration: 3000, isClosable: true });
      setLoading(false);
      return;
    }
    const newCollaborator = {
      userId: userToAdd.userId,
      lodge: userToAdd?.lodge,
      obedience: userToAdd?.obedience,
      photo: userToAdd?.photo,
      name: userToAdd.name,
    };
    await client.post("/colaborators", newCollaborator);
    setCollaborators([...collaborators, newCollaborator]);
    toast({ title: "Colaborador adicionado!", description: `${newCollaborator.name} agora é um colaborador.`, status: "success", duration: 3000, isClosable: true });
    setLoading(false);
  };

  const handleRemoveCollaborator = async (colaborator) => {
    await client.delete(`/colaborators/${colaborator.colaboratorId}`);
    setCollaborators(collaborators.filter((c) => c.colaboratorId !== colaborator.colaboratorId));
    toast({ title: "Colaborador removido.", status: "info", duration: 3000, isClosable: true });
  };

  return (
    <Layout>
      {/* Hero */}
      <Box
        bgGradient="linear(to-br, green.700, green.500)"
        py={[10, 16]}
        px={[4, 6, 8]}
        textAlign="center"
      >
        <Heading color="white" fontSize={["1.8rem", "2.5rem"]} mb={3}>
          Nossos Colaboradores
        </Heading>
        <Text color="green.100" fontSize={["1rem", "1.15rem"]} maxW="600px" mx="auto">
          Um agradecimento especial a todos que estão colaborando para a realização da XXX Edição da Jornada Maçônica do Brasil.
        </Text>
        <Badge
          mt={4}
          colorScheme="yellow"
          fontSize="0.9rem"
          px={4}
          py={1}
          borderRadius="full"
        >
          {collaborators.length} colaboradores
        </Badge>
      </Box>

      <Box maxW="1200px" mx="auto" px={[4, 6, 8]} py={[8, 12]}>
        {user?.type === process.env.REACT_APP_ADMINISTRATOR && (
          <AdminSection onAdd={handleAddCollaborator} />
        )}

        {loading ? (
          <Flex justify="center" align="center" height="200px">
            <Spinner size="xl" color="green.500" />
          </Flex>
        ) : collaborators.length === 0 ? (
          <Box textAlign="center" py={16} color="gray.400">
            <Text fontSize="1.1rem">Nenhum colaborador cadastrado ainda.</Text>
          </Box>
        ) : (
          <SimpleGrid columns={{ base: 2, sm: 3, md: 4, lg: 5 }} spacing={[4, 6]}>
            {collaborators.map((c) => (
              <Box
                key={c.colaboratorId}
                textAlign="center"
                bg="white"
                borderRadius="1.25rem"
                p={5}
                boxShadow="0 2px 12px rgba(0,0,0,0.07)"
                border="1px solid"
                borderColor="gray.100"
                position="relative"
                transition="all 0.25s"
                _hover={{ boxShadow: "0 8px 32px rgba(0,0,0,0.12)", transform: "translateY(-4px)" }}
              >
                {user?.type === process.env.REACT_APP_ADMINISTRATOR && (
                  <IconButton
                    aria-label="Remover colaborador"
                    icon={<DeleteIcon />}
                    size="xs"
                    colorScheme="red"
                    variant="ghost"
                    position="absolute"
                    top={2}
                    right={2}
                    borderRadius="50%"
                    onClick={() => handleRemoveCollaborator(c)}
                  />
                )}
                <Avatar
                  size="xl"
                  src={`https://${bucket}.s3.${region}.amazonaws.com/${folder}/${c.photo}`}
                  mb={3}
                  border="3px solid"
                  borderColor="green.300"
                  boxShadow="0 4px 12px rgba(0,0,0,0.12)"
                />
                <Text fontWeight={700} fontSize="0.9rem" color="gray.800" noOfLines={2}>
                  {c.name}
                </Text>
                {c?.lodge && (
                  <Text fontSize="0.72rem" color="gray.500" mt={1} noOfLines={2}>
                    {c.lodge}
                  </Text>
                )}
                {c?.obedience && (
                  <Text fontSize="0.68rem" color="green.600" fontWeight={600} mt={0.5} noOfLines={1}>
                    {c.obedience}
                  </Text>
                )}
              </Box>
            ))}
          </SimpleGrid>
        )}
      </Box>
    </Layout>
  );
};

export default Colaborators;
